import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@clerk/react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { GitBranch, Plus, Trash2, Loader2, Info, ShieldCheck } from "lucide-react";

const STAGES = [
  { value: "finance_review", label: "Finance Review" },
  { value: "hr_review", label: "HR Review" },
  { value: "sport_admin_review", label: "Sport Admin Review" },
  { value: "legal_review", label: "Legal Review" },
  { value: "compliance_review", label: "Compliance Review (optional)" },
  { value: "ad_review", label: "AD Review" },
  { value: "signature", label: "Signature" },
] as const;

type Stage = typeof STAGES[number]["value"];

interface Rule {
  id: number;
  name: string;
  stage: Stage;
  approverEmails: string[];
  quorum: "all" | "any";
  conditionType: "always" | "salary_gte" | "salary_lt" | "contract_type" | "sport";
  conditionValue: string | null;
  active: boolean;
  priority: number;
}

const CONDITIONS = [
  { value: "always", label: "Always apply" },
  { value: "salary_gte", label: "Salary ≥" },
  { value: "salary_lt", label: "Salary <" },
  { value: "contract_type", label: "Contract type =" },
  { value: "sport", label: "Sport =" },
] as const;

export function ApprovalRulesSettings({ isAdmin }: { isAdmin: boolean }) {
  const { getToken } = useAuth();
  const { toast } = useToast();
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [complianceRequired, setComplianceRequired] = useState<boolean | null>(null);
  const [complianceSaving, setComplianceSaving] = useState(false);

  const [name, setName] = useState("");
  const [stage, setStage] = useState<Stage>("finance_review");
  const [emails, setEmails] = useState("");
  const [quorum, setQuorum] = useState<"all" | "any">("all");
  const [conditionType, setConditionType] = useState<Rule["conditionType"]>("always");
  const [conditionValue, setConditionValue] = useState("");
  const [priority, setPriority] = useState("100");
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const [rulesRes, toggleRes] = await Promise.all([
        fetch("/api/approval-rules", { headers: { Authorization: `Bearer ${token}` } }),
        fetch("/api/approval-rules/compliance-required", { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (rulesRes.ok) setRules(await rulesRes.json());
      if (toggleRes.ok) {
        const data = (await toggleRes.json()) as { enabled?: boolean };
        setComplianceRequired(Boolean(data.enabled));
      }
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  async function handleToggleComplianceRequired(next: boolean) {
    setComplianceSaving(true);
    const prev = complianceRequired;
    setComplianceRequired(next);
    try {
      const token = await getToken();
      const res = await fetch("/api/approval-rules/compliance-required", {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ enabled: next }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error ?? `Error ${res.status}`);
      }
      toast({
        title: next ? "Compliance review required" : "Compliance review optional",
        description: next
          ? "Every contract will route through Compliance before signature."
          : "Contracts will skip Compliance unless a custom rule matches.",
      });
      await refresh();
    } catch (err) {
      setComplianceRequired(prev);
      toast({ title: "Update failed", description: err instanceof Error ? err.message : "Error", variant: "destructive" });
    } finally {
      setComplianceSaving(false);
    }
  }

  useEffect(() => { refresh(); }, [refresh]);

  async function handleCreate() {
    const emailList = emails.split(",").map((e) => e.trim().toLowerCase()).filter((e) => e.includes("@"));
    if (!name.trim() || emailList.length === 0) {
      toast({ title: "Missing fields", description: "Provide a name and at least one approver email.", variant: "destructive" });
      return;
    }
    if (conditionType !== "always" && !conditionValue.trim()) {
      toast({ title: "Condition value required", variant: "destructive" });
      return;
    }
    setCreating(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/approval-rules", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: name.trim(),
          stage,
          approverEmails: emailList,
          quorum,
          conditionType,
          conditionValue: conditionType === "always" ? null : conditionValue.trim(),
          priority: parseInt(priority, 10) || 100,
          active: true,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error ?? `Error ${res.status}`);
      }
      toast({ title: "Rule created" });
      setName("");
      setEmails("");
      setConditionValue("");
      setConditionType("always");
      setPriority("100");
      await refresh();
    } catch (err) {
      toast({ title: "Create failed", description: err instanceof Error ? err.message : "Error", variant: "destructive" });
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(id: number) {
    try {
      const token = await getToken();
      const res = await fetch(`/api/approval-rules/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Delete failed");
      await refresh();
    } catch (err) {
      toast({ title: "Delete failed", description: err instanceof Error ? err.message : "Error", variant: "destructive" });
    }
  }

  async function handleToggleActive(rule: Rule) {
    try {
      const token = await getToken();
      const res = await fetch(`/api/approval-rules/${rule.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ active: !rule.active }),
      });
      if (!res.ok) throw new Error("Update failed");
      await refresh();
    } catch (err) {
      toast({ title: "Update failed", description: err instanceof Error ? err.message : "Error", variant: "destructive" });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GitBranch className="h-5 w-5" /> Approval Rules
        </CardTitle>
        <CardDescription>
          Define which approvers handle each stage. Rules with conditions only fire when the contract matches; multiple matching rules at the same stage are merged. If no rule matches a stage, that stage is skipped.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-start justify-between gap-3 rounded-lg border p-3 bg-muted/30">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Require Compliance review on every contract</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" aria-label="What does this do?" className="text-muted-foreground hover:text-foreground">
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    By default, the Compliance stage is skipped unless a Compliance approval rule matches the contract. Turning this on adds a catch-all rule so every contract routes through Compliance before Signature. Custom Compliance rules you've added are preserved either way.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {complianceRequired
                ? "Every contract will route through Compliance before signature."
                : "Compliance is currently optional — contracts skip it unless a custom rule matches."}
            </p>
          </div>
          {isAdmin ? (
            <Switch
              checked={complianceRequired === true}
              disabled={complianceRequired === null || complianceSaving}
              onCheckedChange={handleToggleComplianceRequired}
              aria-label="Require Compliance review"
            />
          ) : (
            <Badge variant="outline">{complianceRequired ? "Required" : "Optional"}</Badge>
          )}
        </div>

        {isAdmin && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Rule name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. High-value AD review" />
              </div>
              <div className="space-y-1.5">
                <Label>Stage</Label>
                <Select value={stage} onValueChange={(v) => setStage(v as Stage)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2 space-y-1.5">
                <Label>Approver emails (comma-separated)</Label>
                <Input value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="ad@uni.edu, deputy@uni.edu" />
              </div>
              <div className="space-y-1.5">
                <Label>Quorum</Label>
                <Select value={quorum} onValueChange={(v) => setQuorum(v as "all" | "any")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All must approve</SelectItem>
                    <SelectItem value="any">Any one approves</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Priority (lower = first)</Label>
                <Input type="number" value={priority} onChange={(e) => setPriority(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Condition</Label>
                <Select value={conditionType} onValueChange={(v) => setConditionType(v as Rule["conditionType"])}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONDITIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {conditionType !== "always" && (
                <div className="space-y-1.5">
                  <Label>{conditionType.startsWith("salary") ? "Amount ($)" : "Value"}</Label>
                  <Input
                    value={conditionValue}
                    onChange={(e) => setConditionValue(e.target.value)}
                    placeholder={conditionType.startsWith("salary") ? "1000000" : conditionType === "contract_type" ? "head_coach" : "Football"}
                  />
                </div>
              )}
            </div>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Create rule
            </Button>
            <Separator />
          </>
        )}

        <div>
          <p className="text-sm font-medium mb-3">Active rules</p>
          {loading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : rules.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No rules defined yet. Without rules, all stages are skipped and contracts go straight to Signature when submitted.</p>
          ) : (
            <div className="space-y-2">
              {rules.map((r) => (
                <div key={r.id} className="flex items-start justify-between gap-3 py-2 px-3 rounded-lg bg-muted/40">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{r.name}</span>
                      <Badge variant="outline">{STAGES.find((s) => s.value === r.stage)?.label ?? r.stage}</Badge>
                      <Badge variant="secondary">{r.quorum === "all" ? "all" : "any"}</Badge>
                      {!r.active && <Badge variant="outline" className="text-muted-foreground">inactive</Badge>}
                      <span className="text-xs text-muted-foreground">priority {r.priority}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 truncate">
                      {r.conditionType === "always" ? "Always" : `When ${CONDITIONS.find((c) => c.value === r.conditionType)?.label} ${r.conditionValue}`}
                      {" · "}{r.approverEmails.join(", ")}
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleToggleActive(r)}>
                        {r.active ? "Disable" : "Enable"}
                      </Button>
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(r.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
